import React from "react";
import {Link} from "react-router-dom";
import DataSource from "devextreme/data/data_source";
import {download, getInvoicePaymentMethod, Invoice} from "pages/invoices/invoices.service";
import {DataGrid} from "devextreme-react/data-grid";
import Setting from "assets/images/svg/settings_border.svg";
import Export from "assets/images/svg/export_data_border.svg";
import InvoicesTabs from "pages/invoices/component/invoices.tabs";
import DxfGridComponent, {DataColumn} from "components/dxf-grid/dxf-grid.component";
import moment from "moment"

import InvoicePdfDialog from "pages/invoices/component/invoice-pdf.dialog";
import {useModal} from "hooks";
import {formatDate, json, withDateFilter} from "helpers/data";
import {useStatus} from "hooks/use-status";
import {Spinner} from "react-bootstrap";
import SortByComponent from "components/sort-by/sort-by.component";
import {DateFilter} from "pages/tickets/ticket.service";
import cookies from "js-cookie";
import {useTranslation} from "react-i18next";
import CanLoadGuard from "components/private-route/can-load.guard";

interface InvoiceTableProps {
  dataSource: DataSource<Invoice>;
  actions: React.ComponentType;
  invoiceStatus: "paid" | "unpaid" | "refunded" | "canceled";
}

function InvoiceGrid({ dataSource, actions: Actions, invoiceStatus }: InvoiceTableProps) {
  const { t } = useTranslation('invoices');
  const dataGridRef = React.useRef<DataGrid | null>(null);
  const [totalCount, setTotalCount] = React.useState(0);
  const { load, isLoading, done, stop } = useStatus();
  const canBeListDownloaded = invoiceStatus === "paid" || invoiceStatus === "refunded";

  React.useEffect(() => {
    dataSource.on("changed", () => setTotalCount(dataSource.totalCount()));
  }, [dataSource]);

  const columns = React.useMemo(() => {
    return invoiceColumns();
    // switch (invoiceStatus) {
    //   case "paid":
    //   case "refunded":
    //     return [...invoiceColumns()];
    //
    //   case "unpaid":
    //   case "canceled":
    //     return invoiceColumns().filter((i) => !i.paid);
    // }
  }, [invoiceStatus]);

  function handleDownload() {
    load();
    const selected = dataGridRef.current?.instance.getSelectedRowKeys() as number[];
    download(selected).then(()=> done()).catch(()=> stop());
  }

  function handleSortByDate(dateString: string) {
    try {

    const date = JSON.parse(dateString);
    if (date.from === 0) dataSource.filter([])
    else{
      dataSource.filter([["Createdate", "GreaterThanOrEquals", date.from], "and", ["-R2-Createdate", "Lessthan", moment(date.to).add(24,'hours')], "date"]);
    }
    dataSource.load();
    } catch (error) {

    }
  }

  return (
    <>
      <div className="header">
        <div id="invoice-pdf" className="put-outside" style={{ width: 1106, height: 1088 }} />
        <div className="page-name">
          <h1>{t('title') as string} ({totalCount})</h1>
        </div>
        <div className="action">
          <SortByComponent
            list={Object.entries(DateFilter).map(([key, value]) => ({
              value: JSON.stringify(value),
              label: t('common:sort.' +key)
            }))}
            onChange={handleSortByDate}
            date
            defaultValue={JSON.stringify(DateFilter.all)}
          />
          {canBeListDownloaded && (
            <button disabled={isLoading} className="download-btn">
              {isLoading ? (
                <Spinner animation="border" size="sm" variant="primary" />
              ) : (
                <img src={Export} alt="Exports" onClick={handleDownload} />
              )}
            </button>
          )}
          <Link to="/paymentSetting">
            <img src={Setting} alt="Setting" />
          </Link>
        </div>
      </div>
      <hr className="hr" />
      <InvoicesTabs />
      <div className="invoiceTable">
        <div className="dataTable">
          <DxfGridComponent
            hideIndexRow
            columns={columns}
            ref={dataGridRef}
            dataSource={dataSource}
            actionsElement={(props) => <CanLoadGuard>
                <Actions {...props}  />
            </CanLoadGuard>}
            scrolling={{ showScrollbar: "always" }}
            selection={{ mode: 'multiple', selectAllMode: 'page' ,showCheckBoxesMode: canBeListDownloaded? "always" : "none"}}
          />
        </div>
      </div>
    </>
  );
}

function renderPaymentMethodCell({ data }: any) {
  const invoice = data as Invoice;
  const payment = getInvoicePaymentMethod(invoice);

  return (
    <p className="m-0">
      <img src={payment?.icon} alt="payment-icon" />
      {payment?.info}
    </p>
  );
}

function InvoiceDetail({ invoice }: { invoice: Invoice }) {
  const [show, open, close] = useModal(false);

  return (
    <>
      <button className="btn-link" onClick={open}>
        {invoice.serial}
      </button>
      <InvoicePdfDialog invoice={invoice} show={show} onHide={close} />
    </>
  );
}

function invoiceColumns(): (DataColumn & { paid?: boolean })[] {
  const lang = cookies.get("i18next");
  return [
    {
      id: "Serial",
      fixed: true,
      fixedPosition: lang === "ar" ? "right" : "left",
      width: 200,
      caption: "invoices:invoice-number",
      alignment: "center",
      cellRender: ({ data }: any) => <InvoiceDetail invoice={data} />
    },
    {
      id: "CreateDate",
      width: 150,
      caption: "invoices:invoice-date",
      calculateDisplayValue: (d: any) => formatDate(d.createDate),
      dataType: "date",
      calculateFilterExpression: withDateFilter("CreateDate")
    },
    {
      id: "StartDate",
      width: 300,
      caption: "invoices:period",
      cellRender: ({ data }: any) => formatDate(data.startDate) + " - " + formatDate(data.endDate),
      dataType: "date",
      calculateFilterExpression: withDateFilter("StartDate")
    },
    {
      id: "InvoiceDetails[VersionName]",
      width: 200,
      caption: "invoices:product-title",
      calculateCellValue: (d: any) => json(d.productTitle)
    },
    { id: "PaymentInfoSearch",dataType:'string', caption: "invoices:payment-method", width: 400, paid: true, cellRender: renderPaymentMethodCell },
    { id: "total", width: 100, caption: "invoices:total", paid: true, dataType: "number" },
    { id: "currency", width: 100, caption: "invoices:currency", paid: true, dataType: "number", cellRender: (cell) => json(cell.value) },
    {
      id: "paymentDate",
      dataType: "date",
      caption: "invoices:payment-date",
      width: 300,
      paid: true,
      calculateFilterExpression: withDateFilter("PaymentDate"),
      calculateDisplayValue: (d: any) => formatDate(d.paymentDate),
    },
    { id: "notes", caption: "invoices:notes", minWidth: 200 },
    { id: "totalVatAmount", width: 100, caption: "invoices:vat", paid: true, dataType: "number" },
  ];
}

export default React.memo(InvoiceGrid);
